import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail } from 'lucide-react';
import { useProAuth } from '@/lib/auth-context';
import { AuthLayout } from '@/components/layout/AuthLayout';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { requestPasswordReset } = useProAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <h2 className="text-xl font-semibold text-neutral-900 mb-1">
        {t('pro.auth.forgot_title', 'Mot de passe oublié')}
      </h2>
      <p className="text-sm text-neutral-500 mb-6">
        {t('pro.auth.forgot_subtitle', 'Recevez un lien de réinitialisation par email')}
      </p>

      {done ? (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
            {t('pro.auth.forgot_sent', 'Email envoyé. Vérifiez votre boîte de réception.')}
          </div>
          <Link
            to="/login"
            className="block w-full text-center py-2.5 rounded-lg text-sm font-medium border border-neutral-200 hover:bg-neutral-50"
          >
            {t('pro.auth.back_to_login', 'Retour à la connexion')}
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-1.5 block">
              {t('pro.auth.email', 'Email')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60 inline-flex items-center justify-center gap-2"
            style={{ background: 'var(--pro-primary)' }}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('pro.auth.send_reset_link', 'Envoyer le lien')}
          </button>

          <Link
            to="/login"
            className="block text-center text-sm text-orange-600 hover:underline"
          >
            {t('pro.auth.back_to_login', 'Retour à la connexion')}
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}
